import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { Workbox } from 'workbox-window';
import { useDispatch, useSelector } from 'react-redux';
import { useIsOnline } from 'react-use-is-online';
import Icon from './Icon';
import InformationBanner from './InformationBanner';
import { isQRCodePassageDeviceType } from '../../devices/helper';
import { sendMessageToServiceWorker } from '../../core/offlineHelpers';
import { notification } from '../../notification/actions';
import LoaderBarTop from './LoaderBarTop';
const wb = new Workbox('/service-worker.js');
// Workspaces where the new version banner is not displayed
const WORKSPACES_WITHOUT_UPDATE_BANNER = ['atlas', 'atlas-test'];

const OfflineBanner = () => {
    const [t] = useTranslation();
    const { isOnline } = useIsOnline();
    const dispatch = useDispatch();
    const isLogged = useSelector(state => state.authentication.isLogged);
    const devices = useSelector(state => state.devices.list);
    const [pendingCount, setPendingCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const pendingCountRef = useRef(pendingCount);
    const notificationShownRef = useRef(false);

    // Ref required to access the updated pendingCount value in the serviceWorkerMessageHandler
    useEffect(() => {
        pendingCountRef.current = pendingCount;
    }, [pendingCount]);

    const showSuccessNotification = useCallback((replayedCount = null) => {
        // Prevent multiple notifications
        if (notificationShownRef.current) {
            return;
        }

        notificationShownRef.current = true;

        let message;
        if (replayedCount > 1) {
            message = t('notifications:flush_pending_passages_on_service_worker_success_plural', { count: replayedCount });
        } else {
            message = t('notifications:flush_pending_passages_on_service_worker_success_singular' , { count: 1 });
        }

        dispatch(notification(
            message,
            'successful',
            'check'
        ));

        notificationShownRef.current = false;
    }, []);

    const flushQueue = useCallback(() => {
        console.log('Offline banner : flushing queue...')
        setIsLoading(true);
        console.log('Offline banner : sendMessageToServiceWorker GET_PENDING_DEVICE_MESSAGES_COUNT');
        sendMessageToServiceWorker(wb, {
            action: 'GET_PENDING_DEVICE_MESSAGES_COUNT'
        }).then(count => {
            console.log(`Offline banner : received GET_PENDING_DEVICE_MESSAGES_COUNT result count ${count}`);
            setPendingCount(count);
            if (isOnline && count > 0) {
                console.log('Offline banner : sendMessageToServiceWorker FLUSH_QUEUE');
                return sendMessageToServiceWorker(wb, {
                    action: 'FLUSH_QUEUE',
                    queueName: 'deviceMessages'
                }).then(flushCount => {
                    console.log(`Offline banner : received FLUSH_QUEUE result count ${flushCount}`);
                    if (flushCount === 0) { // If the queue is empty, show the success notification
                        showSuccessNotification(pendingCountRef.current);
                        setPendingCount(0);
                    }
                    return flushCount;
                });
            }
            return count;
        }).catch(error => {
            console.log('Offline banner : error during flush queue:', error);
        }).finally(() => {
            console.log(`Offline banner : isLoading false`);
            setIsLoading(false);
        });

    }, []);

    // Explicitly requests the number of messages waiting to load to Service Worker
    const requestServiceWorkerPendingMessageCount = () => {
        if (isMobile
            && navigator.serviceWorker
            && navigator.serviceWorker.controller
            && isLogged
            && devices.some(device => isQRCodePassageDeviceType(device))
        ) {
            console.log('OfflineBanner : requestServiceWorkerPendingMessageCount GET_PENDING_DEVICE_MESSAGES_COUNT ');
            sendMessageToServiceWorker(wb, {
                action: 'GET_PENDING_DEVICE_MESSAGES_COUNT'
            }).then(count => {
                console.log(`OfflineBanner : requestServiceWorkerPendingMessageCount received GET_PENDING_DEVICE_MESSAGES_COUNT count ${count} `);
                setPendingCount(count);
                if (isOnline && count > 0) {
                    flushQueue();
                }
            });
        }
    };

    // Service Worker can also send the pending count itself to the app in the event of a change
    const serviceWorkerMessageHandler = event => {
        console.log(`OfflineBanner : received event type  ${event.data?.type} value ${event.data?.value}`);
        console.log(`OfflineBanner : current count is  ${pendingCountRef.current}`);

        if (event.data && event.data.type === 'BROADCAST_PENDING_DEVICE_MESSAGES_COUNT') {
            if (event.data.value === 0 && pendingCountRef.current > 0) {
                showSuccessNotification(pendingCountRef.current);
            }

            setPendingCount(event.data.value);
        }
    };

    useEffect(() => {
        console.log('OfflineBanner useEffect')

        if ('serviceWorker' in navigator && navigator.serviceWorker !== undefined) {
            console.log('OfflineBanner : Serviceworker is supported by browser')
            if (navigator.serviceWorker.controller === null) {
                console.log('OfflineBanner : No controlling serviceworker yet, refresh the page')
                // No controlling serviceworker yet, must refresh first
                setShowUpdate(true);
            } else {
                // Already controlling serviceworker, check for updates
                // updateViaCache ensures that the browser always checks for the latest version of the serviceworker
                wb.register({ updateViaCache: 'none' }).then(registration => {
                    // Explicitly requests the number of messages waiting to load
                    requestServiceWorkerPendingMessageCount(wb);
                    console.log('OfflineBanner : Check for serviceworker latest version...')

                    // Listening for changes in the status of the installed worker service
                    registration.addEventListener('updatefound', () => {
                        console.log('OfflineBanner : ServiceWorker updatefound : event received')
                        const installingWorker = registration.installing;
                        if (installingWorker) {
                            console.log('OfflineBanner : ServiceWorker updatefound : serviceworker is installing...')
                            installingWorker.onstatechange = () => {
                                if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                    console.log('OfflineBanner : ServiceWorker updatefound : serviceworker was installed')
                                    // New content is available : prompts the user to update the page
                                    setShowUpdate(true);
                                }
                            };
                        }
                    });


                    console.log('OfflineBanner : subscribe to 1')
                    // Then subscribe to receive updates
                    navigator.serviceWorker.addEventListener('message', serviceWorkerMessageHandler);
                    return () => {
                        //navigator.serviceWorker.removeEventListener('message', serviceWorkerMessageHandler);
                    };
                });
                console.log('OfflineBanner : subscribe to2')
                // Then subscribe to receive updates
                navigator.serviceWorker.addEventListener('message', serviceWorkerMessageHandler);
                const messageChannel = new MessageChannel();
                messageChannel.port1.addEventListener('message', (event) => {
                  console.log('SETSHOWUPDATE 8', event.data.showUpdate);
                  if (event.data.showUpdate) {
                    setShowUpdate(event.data.showUpdate === 'true');
                  }
                });
                messageChannel.port1.start();
                navigator.serviceWorker.controller.postMessage({ action: 'SHOW_UPDATE' }, [messageChannel.port2]);

                return () => { };
            }
            console.log('OfflineBanner : subscribe to3')
            // Then subscribe to receive updates
            navigator.serviceWorker.addEventListener('message', serviceWorkerMessageHandler);
        }
        return () => { };
    }, []);

    useEffect(() => {
        // Fetch pending count at each status change (off or on)
        requestServiceWorkerPendingMessageCount(wb);
    }, [isOnline]);

    let icon = <Icon componentName={'Icon'} type={'faWifiSlash'} />;
    const hasPendingCount = pendingCount > 0;
    let primaryLabel = '';
    let secondaryLabel = '';
    let buttonLabel = null;
    let onClick = null;

    const canShowUpdate = !WORKSPACES_WITHOUT_UPDATE_BANNER.includes(localStorage.getItem('iot_rocket_workspace'));

    if (showUpdate && isOnline && canShowUpdate) {
        console.log('OfflineBanner : showUpdate for serviceworker and isOnline')
        icon = <Icon componentName={'Icon'} type={'faArrowsRotate'} />;
        primaryLabel = t('common:new_application_version_available');
        buttonLabel = t('common:reload_client_serviceworker');
        onClick = () => {
            console.log('OfflineBanner : onClick : Clicked on reload');
            if (!navigator.serviceWorker.controller) {
                // Si aucun SW ne contrôle la page, on force le rechargement
                console.log('OfflineBanner : onClick : No serviceworker controlling the page, reload the page');
                window.location.reload();
            } else {
                // updateViaCache ensures that the browser always checks for the latest version of the serviceworker
                console.log('OfflineBanner : onClick : A serviceworker is already controlling the page');
                wb.register({ updateViaCache: 'none' })
                    .then(() => sendMessageToServiceWorker(wb, { action: 'RELOAD_CLIENT_SERVICEWORKER' }))
                    .then(result => {
                        if (result.status === 'success') {
                            console.log('OfflineBanner : onClick : RELOAD_CLIENT_SERVICEWORKER sent and success received. Reloading the page');
                            window.location.reload();
                        } else {
                            dispatch(notification(t('notifications:reload_client_serviceworker_error'), 'error', 'frown'));
                        }
                    });
            }
        };
    } else {
        // ONLINE CASES
        // Cases where no banner should be displayed:
        if (isOnline && (!isMobile || !hasPendingCount)) {
            console.log('OfflineBanner : We are online, but not on mobile or no count to display. No banner displayed');
            return <></>;
        }
        if (isOnline && isMobile && hasPendingCount) {
            console.log('OfflineBanner : We are online, on mobile and we have pending count to display. Banner displayed');
            primaryLabel = null
            secondaryLabel = t(pendingCount === 1 ? 'common:offline_mobile_warning_with_passage' : 'common:offline_mobile_warning_with_passages', { pendingCount });

        }
        // OFFLINE CASES
        if (!isOnline) {
            console.log('OfflineBanner : We are offline');
            primaryLabel = t('common:offline_warning');
            if (!isMobile) {
                console.log('OfflineBanner : We are on desktop');
                secondaryLabel = t('common:offline_desktop_warning');
            } else {
                console.log('OfflineBanner : We are on mobile');
                console.log(`Pending count is ${pendingCount}`);

                if (hasPendingCount) {
                    secondaryLabel = t(pendingCount === 1 ? 'common:offline_mobile_warning_with_passage' : 'common:offline_mobile_warning_with_passages', { pendingCount });
                }
            }
        }
    }

    return (
        <>
            <LoaderBarTop isLoading={isLoading} />
            <InformationBanner
                icon={icon}
                primaryLabel={primaryLabel}
                secondaryLabel={secondaryLabel}
                buttonLabel={buttonLabel}
                onClick={onClick}
            />
        </>
    );
};

export default OfflineBanner;